import { Fragment, useEffect, useMemo, useRef, useState } from "react"

// ** Third Party Components
import { AlertCircle, Info, Loader2, PenTool } from "lucide-react"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

// ** Theme Components
import { Badge } from "../common/badge"
import { Input } from "../common/input"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { DataTableColumnHeader } from "../common/data-table/data-table-column-header"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../common/table"

// ** Utils
import { cn } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import AccumulatedBillAmounts from "./accumulated-bill-amounts"

interface BoqTablePreviewProps {
  title: string
  data: any[]
  showStatus?: boolean
  handleAmountChange?: (id: string, amount: string) => void
  adjustments?: Record<string, string>
  isApprove?: boolean
}

const BoqTablePreview = ({
  title,
  data,
  adjustments,
  isApprove = false,
  showStatus = false,
  handleAmountChange,
}: BoqTablePreviewProps) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const [activeInputId, setActiveInputId] = useState<string | null>(null)

  const columns = useMemo((): ColumnDef<any>[] => {
    const baseColumns: ColumnDef<any>[] = [
      {
        accessorKey: "number",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Section')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          const itemNumber = item._boq_item.number
          return (
            <div className='flex gap-1'>
              <span className='text-xs sm:text-sm'>{itemNumber}</span>
            </div>
          )
        },
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Description')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          const itemDescription = item?._boq_item?.description
          return (
            <div className='max-w-[200px] text-xs leading-tight sm:max-w-xs sm:text-sm lg:min-w-[250px]'>
              {itemDescription}
            </div>
          )
        },
        size: 300,
        enableSorting: false,
      },
      ...(showStatus
        ? [
            {
              accessorKey: "status",
              header: ({ column }: any) => <DataTableColumnHeader column={column} title={translate('Status')} />,
              cell: ({ row }: any) => {
                const bill = row?.original.bill

                if (bill?.amount_by_signer) {
                  return (
                    <Badge variant='secondary' className='flex min-w-[100px] justify-center gap-1'>
                      <PenTool className='h-3 w-3' /> {translate("Signed")}
                    </Badge>
                  )
                }

                if (
                  bill?.billStatus === "declined" ||
                  (bill?.billStatus === "paid" && bill?.amount_by_signer < 1) ||
                  bill?.is_declined ||
                  bill?.declined
                ) {
                  return (
                    <Badge
                      variant='destructive'
                      className='flex min-w-[100px] justify-center gap-1'
                    >
                      <AlertCircle className='h-3 w-3' /> {translate("Declined")}
                    </Badge>
                  )
                }

                if (bill?.approved_amount >= bill?.amount) {
                  return (
                    <Badge variant='secondary' className='flex min-w-[100px] justify-center'>
                      {translate("Approved")}
                    </Badge>
                  )
                }

                if (bill?.approved_amount > 0) {
                  return (
                    <Badge variant='default' className='flex min-w-[100px] justify-center gap-1'>
                      <Info className='h-3 w-3' /> {translate("Partially Approved")}
                    </Badge>
                  )
                }

                return (
                  <Badge variant='disabled' className='flex min-w-[100px] justify-center gap-1'>
                    <Loader2 className='h-3 w-3 animate-spin' /> {translate("Pending")}
                  </Badge>
                )
              },
              enableSorting: false,
            },
          ]
        : []),
      {
        accessorKey: "units",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Unit')} className='text-center' />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          const itemUnit = item?._boq_item?.units
          return <div className='text-center text-xs sm:text-sm'>{itemUnit}</div>
        },
        enableSorting: false,
      },
      {
        accessorKey: "boq_cost",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Price per unit')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='min-w-fit text-center text-xs sm:text-sm'>
              {item?._boq_item?.is_item && (
                <Tooltip>
                  <TooltipTrigger>
                    <span className='flex items-center gap-1'>
                      {formatCurrency(item?._boq_item?.discount_price, isHebrew)}
                      <small className='text-[10px]'>
                        ({Number(((item?._boq_item?.total_discount ?? 0) * 100).toFixed(2))}%)
                      </small>{" "}
                    </span>{" "}
                    {item?.total_discount ? (
                      <span className='text-xs text-slate-500 line-through sm:text-sm'>
                        {formatCurrency(item?._boq_item?.boq_cost, isHebrew)}
                      </span>
                    ) : (
                      <></>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {translate("Discount applied to price")}:{" "}
                    {Number(((item?._boq_item?.total_discount ?? 0) * 100).toFixed(2))}%
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )
        },
        size: 150,
        enableSorting: false,
      },
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Amount from estimate')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='text-center text-xs leading-tight sm:text-sm'>
              {item?._boq_item?.amount || ""}
            </div>
          )
        },
        size: 100,
        enableSorting: false,
      },
      {
        accessorKey: "paid",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Amount')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='text-center text-xs sm:text-sm'>
              {item?._boq_item?.is_item ? (
                <AccumulatedBillAmounts bill={item}>{item?._boq_item?.paid}</AccumulatedBillAmounts>
              ) : (
                <></>
              )}
            </div>
          )
        },
        size: 50,
        enableSorting: false,
      },
      {
        accessorKey: "current_bill",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Current Bill')} />,
        cell: ({ row }: any) => {
          const item = row?.original
          return item?._boq_item?.is_item ? (
            <div className='flex min-w-[120px] flex-col text-center text-xs sm:min-w-fit sm:text-sm'>
              <span className='text-xs text-primary'>
                {translate("Requested")}: {item?.amount}
              </span>
              <span className='text-xs text-green-600'>
                {translate("Bill Approved")}: {item?.approved_amount}
              </span>
            </div>
          ) : null
        },
        size: 100,
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "adjustment_input",
        header: ({ column }) => <DataTableColumnHeader column={column} title={title} />,
        cell: ({ row }: any) => {
          const item = row?.original
          return item?._boq_item?.is_item ? (
            <BillAmountInput
              item={item}
              billAmounts={adjustments}
              handleAmountChange={handleAmountChange}
              activeInputId={activeInputId}
              setActiveInputId={setActiveInputId}
              isApprove={isApprove}
            />
          ) : null
        },
        size: 100,
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "current_sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Current Sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return item?._boq_item?.is_item ? (
            <div className='flex flex-col items-center gap-1'>
              <span className='text-xs'>
                {translate("Requested")}:{" "}
                {formatCurrency(item?._boq_item?.discount_price * item?.amount, isHebrew)}
              </span>
              <span className='text-xs text-green-600'>
                {translate(item?.is_signed ? "Signed" : "Approved")}:{" "}
                {formatCurrency(item?._boq_item?.discount_price * item?.approved_amount, isHebrew)}
              </span>
            </div>
          ) : null
        },
        size: 200,
        enableSorting: false,
      },
      {
        accessorKey: "estimated_sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Boq sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return item?._boq_item?.is_item ? (
            <div className='text-center text-xs font-bold sm:text-sm'>
              <span className=''>{formatCurrency(item?._boq_item?.real_sum, isHebrew)}</span>
            </div>
          ) : null
        },
        enableSorting: false,
      },
      {
        accessorKey: "sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          const amount = isApprove ? item?.approved_amount : item?.amount
          const originalAmount = Number.parseFloat(amount)
          const newAmount = adjustments
            ? !isNaN(Number.parseFloat(adjustments[item.id]))
              ? Number.parseFloat(adjustments[item.id])
              : amount
            : 0
          const billDifference = newAmount ? newAmount - originalAmount : 0
          return item?._boq_item?.is_item ? (
            <div
              className={cn(
                "text-center text-xs sm:text-sm",
                billDifference >= 0 ? "text-green-600" : "text-red-600",
              )}
            >
              {formatCurrency(item?._boq_item?.paid_sum, isHebrew)}
            </div>
          ) : null
        },
        enableSorting: false,
      },
    ]

    return baseColumns
  }, [isHebrew, adjustments, title, handleAmountChange, activeInputId, translate])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
  })

  return (
    <Fragment>
      <div className='overflow-x-auto rounded-md border'>
        <Table className='min-w-[800px] text-xs sm:text-sm'>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className='border-b bg-gray-50'>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "border-gray-200 px-1 py-2 text-center text-gray-900 sm:px-2 sm:py-3",
                      isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                    )}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? "flex cursor-pointer select-none items-center justify-center gap-1"
                            : "flex items-center justify-center"
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, index) => {
              const rowData = row.original
              const isParent = rowData.hasChildren
              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    `border-b hover:bg-gray-50 ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-25"
                    } ${isParent ? "font-medium" : ""}`,
                    rowData?.bill?.amount && "border-y border-primary bg-primary/5",
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "border-gray-200 px-1 py-1.5 sm:px-2 sm:py-2",
                        isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <div className='flex flex-col items-center justify-between gap-2 py-4 sm:flex-row sm:items-center'>
        <div className='text-xs text-muted-foreground sm:text-sm'>
          {translate("Showing")} {table.getFilteredRowModel().rows.length} {translate("of")}{" "}
          {data.length} {translate("rows")}
        </div>
      </div>
    </Fragment>
  )
}

export default BoqTablePreview

function BillAmountInput({
  item,
  billAmounts,
  handleAmountChange,
  activeInputId,
  setActiveInputId,
  isApprove,
}: any) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (activeInputId === item?.id) {
      inputRef.current?.focus()
    }
  }, [activeInputId, item?.id])

  const amount = billAmounts[item?.id]
    ? billAmounts[item?.id]
    : isApprove
      ? item?.approved_amount
      : item?.amount

  return (
    <Input
      type='number'
      placeholder={amount}
      ref={inputRef}
      min={0}
      className='w-full border-primary sm:w-[10ch]'
      onFocus={() => setActiveInputId(item?.id)}
      onChange={(e) => {
        handleAmountChange(item?.id, Number(e.target.value))
        setActiveInputId(item?.id)
      }}
      value={billAmounts[item?.id]}
    />
  )
}
